import * as THREE from "three";

import {
  hasShieldAura,
  hasTrailBoost,
  resolveProjectileTint,
  resolveShipWash,
} from "../content/affix-identity";
import { pickupCollectFlashColor } from "../content/pickup-identity";
import type { DropState, WorldSnapshot } from "../simulation/snapshot";
import { disposeObject } from "../core/scene-lifecycle";

type TrailBoost = { color: number; sizeMult: number };

/**
 * Ship-local affix presentation: projectile tint, emissive wash, shield aura, pickup flash.
 */
export class AffixShipFx {
  private shipRoot: THREE.Group | null = null;
  private glowMats: THREE.MeshStandardMaterial[] = [];
  private engineMats: THREE.MeshStandardMaterial[] = [];
  private readonly aura: THREE.Mesh;
  private owned: readonly string[] = [];
  private pickupFlash = 0;
  private pickupAffixId: string | null = null;
  private pickupKind: DropState["kind"] | null = null;
  private simTime = 0;
  private lastWash: number | null = null;
  private lastShield = false;
  private lastAuraColor = -1;

  public constructor() {
    this.aura = new THREE.Mesh(
      new THREE.RingGeometry(2.2, 2.55, 32),
      new THREE.MeshBasicMaterial({
        color: 0x6ec6ff,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    this.aura.rotation.x = -Math.PI / 2;
    this.aura.visible = false;
  }

  public captureShip(root: THREE.Group): void {
    this.shipRoot = root;
    this.glowMats = [];
    this.engineMats = [];
    root.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mat = obj.material;
      if (!(mat instanceof THREE.MeshStandardMaterial)) return;
      if (mat.emissiveIntensity >= 0.7 && mat.emissive.getHex() !== 0x4a3008) {
        this.engineMats.push(mat);
      } else if (mat.emissiveIntensity > 0) {
        this.glowMats.push(mat);
      }
    });
    if (!root.children.includes(this.aura)) root.add(this.aura);
  }

  public sync(snap: WorldSnapshot): void {
    this.owned = snap.runInventory.affixes;
    this.pickupFlash = snap.pickupFlash;
    this.pickupAffixId = snap.pickupFlashAffixId;
    this.pickupKind = snap.pickupFlashKind;
    this.simTime = snap.simTime;
  }

  public projectileTint(): number | null {
    return resolveProjectileTint(this.owned);
  }

  public trailBoost(baseColor: number): TrailBoost {
    if (!hasTrailBoost(this.owned)) {
      return { color: baseColor, sizeMult: 1 };
    }
    return { color: 0xff8a4a, sizeMult: 1.35 };
  }

  public place(_x: number, _y: number, _altitude: number, hullFacing: number): void {
    if (!this.shipRoot) return;

    const wash = resolveShipWash(this.owned);
    const shield = hasShieldAura(this.owned);
    const pulse = 0.55 + 0.45 * Math.sin(this.simTime * 3.2);
    const washChanged = wash !== this.lastWash;
    this.lastWash = wash;

    for (const mat of this.glowMats) {
      if (wash != null) {
        if (washChanged) mat.emissive.setHex(wash);
        mat.emissiveIntensity = 0.55 + pulse * 0.25;
      } else {
        if (washChanged) mat.emissive.setHex(0x4a3008);
        mat.emissiveIntensity = 0.75;
      }
    }

    for (const mat of this.engineMats) {
      if (wash != null) {
        if (washChanged) mat.emissive.setHex(wash);
        mat.emissiveIntensity = 0.75 + pulse * 0.35;
      } else {
        if (washChanged) mat.emissive.setHex(0xa04a20);
        mat.emissiveIntensity = 0.8;
      }
    }

    if (this.pickupFlash > 0.02) {
      const flashColor = pickupCollectFlashColor(this.pickupKind, this.pickupAffixId);
      const flashGain = this.pickupFlash * 1.4;
      for (const mat of this.glowMats) {
        mat.emissive.setHex(flashColor);
        mat.emissiveIntensity = Math.max(mat.emissiveIntensity, flashGain);
      }
      for (const mat of this.engineMats) {
        mat.emissive.setHex(flashColor);
        mat.emissiveIntensity = Math.max(mat.emissiveIntensity, flashGain);
      }
      this.lastWash = null; // force wash reapply after flash
    }

    if (shield !== this.lastShield) {
      this.aura.visible = shield;
      this.lastShield = shield;
    }
    if (shield) {
      this.aura.position.set(0, -0.15, 0);
      this.aura.rotation.y = -hullFacing;
      const auraMat = this.aura.material as THREE.MeshBasicMaterial;
      const auraColor = this.owned.includes("shield_weave") ? 0x8ad4ff : 0x6ec6ff;
      if (auraColor !== this.lastAuraColor) {
        auraMat.color.setHex(auraColor);
        this.lastAuraColor = auraColor;
      }
      auraMat.opacity = 0.12 + pulse * 0.1;
    }
  }

  public dispose(): void {
    if (this.shipRoot?.children.includes(this.aura)) {
      this.shipRoot.remove(this.aura);
    }
    disposeObject(this.aura);
    this.shipRoot = null;
    this.glowMats = [];
    this.engineMats = [];
  }
}
